import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type AdminPermission = 'dashboard' | 'users' | 'jobs' | 'verify' | 'payments' | 'subscriptions' | 'contracts' | 'disputes' | 'messages' | 'drafts' | 'tracking' | 'instagram-import' | 'social-media' | 'feedback' | 'labeling';

interface AdminPermissionsData {
  isSuperAdmin: boolean;
  permissions: AdminPermission[] | null;
}

export function useAdminPermissions() {
  const { user, userType } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-permissions', user?.id],
    queryFn: async (): Promise<AdminPermissionsData> => {
      if (!user?.id || userType !== 'admin') {
        return { isSuperAdmin: false, permissions: [] };
      }

      const { data: adminRow } = await supabase
        .from('admin')
        .select('is_super_admin, permissions')
        .eq('user_id', user.id)
        .single();

      return {
        isSuperAdmin: adminRow?.is_super_admin ?? false,
        permissions: (adminRow?.permissions as AdminPermission[] | null) ?? null,
      };
    },
    enabled: !!user?.id && userType === 'admin',
    staleTime: 5 * 60 * 1000,
  });

  const hasPermission = (key: AdminPermission): boolean => {
    if (!data) return false;
    if (data.isSuperAdmin) return true;
    if (data.permissions === null) return true;
    return data.permissions.includes(key);
  };

  return {
    isLoading,
    isSuperAdmin: data?.isSuperAdmin ?? false,
    permissions: data?.permissions ?? null,
    hasPermission,
  };
}
